/**
 * Pure USA Pickleball traditional side-out helpers (deterministic).
 * Court L/R for the serving team ARE rules-persistent (exception to ADR-033 TT derive model).
 */

/** @typedef {'A'|'B'} Side */
/** @typedef {'right'|'left'} ServeCourt */

/** Even score → right court; odd → left (server's OWN team score). */
export function serveCourtForScore(serverScore) {
  return Number(serverScore) % 2 === 0 ? "right" : "left";
}

export function otherSide(side) {
  return side === "A" ? "B" : "A";
}

/** Toggle LEFT/RIGHT for both partners (legacy helper; prefer placeServerForScore after points). */
export function swapPartnerCourts(side) {
  return {
    ...side,
    rightPlayerId: side.leftPlayerId,
    leftPlayerId: side.rightPlayerId,
  };
}

export function courtOfPlayer(side, playerId) {
  if (!playerId) return "right";
  if (side.leftPlayerId && side.leftPlayerId === playerId) return "left";
  return "right";
}

export function playerInCourt(side, court) {
  return court === "left" ? side.leftPlayerId || side.rightPlayerId : side.rightPlayerId;
}

export function partnerId(side, playerId) {
  if (!playerId) return side.rightPlayerId || side.leftPlayerId || null;
  if (side.rightPlayerId === playerId) return side.leftPlayerId || null;
  if (side.leftPlayerId === playerId) return side.rightPlayerId || null;
  return side.leftPlayerId || side.rightPlayerId || null;
}

/**
 * After the serving team scores: place the continuing server in the court
 * matching the NEW score (even=right, odd=left); partner opposite.
 * This is the official positioning rule — not a blind toggle when Server #2
 * was serving from the "wrong" parity court.
 *
 * @param {object} side
 * @param {string|null} serverId
 * @param {number} score
 */
export function placeServerForScore(side, serverId, score) {
  const court = serveCourtForScore(score);
  const partner = partnerId(side, serverId);
  if (court === "right") {
    return {
      ...side,
      rightPlayerId: serverId || side.rightPlayerId,
      leftPlayerId: partner,
    };
  }
  return {
    ...side,
    leftPlayerId: serverId || side.leftPlayerId,
    rightPlayerId: partner,
  };
}

/**
 * Side-out transition: opponent serves as server #1 from court matching THEIR score.
 * Receiving (former serving) team does not move.
 *
 * @param {object} game
 * @param {'singles'|'doubles'} mode
 */
export function applySideOutTransition(game, mode = game.mode) {
  const nextSide = otherSide(game.servingSide);
  const recv = nextSide === "A" ? game.sideA : game.sideB;
  const score = recv.score || 0;
  const serveCourt = serveCourtForScore(score);
  const serverId =
    mode === "singles"
      ? recv.players?.[0]?.id || recv.rightPlayerId || null
      : playerInCourt(recv, serveCourt) || recv.players?.[0]?.id || null;
  return {
    servingSide: nextSide,
    serverNumber: 1,
    serverId,
    serveCourt,
  };
}

/**
 * Doubles: server #1 fault → partner as #2 from their current court (no swap).
 * @param {object} game
 */
export function advanceToSecondServerTransition(game) {
  const serving = game.servingSide === "A" ? game.sideA : game.sideB;
  const nextServerId = partnerId(serving, game.serverId);
  return {
    serverNumber: 2,
    serverId: nextServerId,
    serveCourt: courtOfPlayer(serving, nextServerId),
  };
}

/**
 * Serving-team point: bump score, place server for new score (doubles), win check.
 * @param {object} game
 * @returns {{ game: object, scored: number, opp: number }}
 */
export function awardServePointTransition(game) {
  const side = game.servingSide;
  const scored = (side === "A" ? game.sideA.score : game.sideB.score) + 1;
  const opp = side === "A" ? game.sideB.score : game.sideA.score;
  let sideA = game.sideA;
  let sideB = game.sideB;
  let serveCourt = serveCourtForScore(scored);

  if (side === "A") {
    sideA = { ...sideA, score: scored };
    if (game.mode === "doubles") {
      sideA = placeServerForScore(sideA, game.serverId, scored);
      serveCourt = courtOfPlayer(sideA, game.serverId);
    }
  } else {
    sideB = { ...sideB, score: scored };
    if (game.mode === "doubles") {
      sideB = placeServerForScore(sideB, game.serverId, scored);
      serveCourt = courtOfPlayer(sideB, game.serverId);
    }
  }

  const gameWinner =
    scored >= game.pointsToWin && scored - opp >= game.winBy ? side : null;

  return {
    sideA,
    sideB,
    serveCourt,
    gameWinner,
    scored,
    opp,
  };
}

/**
 * Serving-team fault: singles → side-out; doubles #1 → #2; doubles #2 → side-out.
 * @param {object} game
 * @returns {'side_out'|'second_server'}
 */
export function servingFaultAction(game) {
  if (game.mode === "singles") return "side_out";
  if (game.serverNumber === 1) return "second_server";
  return "side_out";
}
